import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import SetupWizard from '../components/SetupWizard';
import {
  Settings as SettingsIcon, Users, Plus, Loader2, Trash2, Edit2,
  Shield, FileText, Eye, EyeOff, KeyRound, Copy, CheckCheck, Link2,
  Mail, UserCheck, UserX, UserMinus, UserPlus, ExternalLink,
  TrendingUp, Calendar, CheckSquare, ShoppingCart, BarChart2, FileDown,
  Target, CheckCircle2, XCircle, LayoutGrid, Sparkles, Warehouse, Factory,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Amministratore' },
  { value: 'sales_manager', label: 'Resp. Commerciale' },
  { value: 'sales_rep', label: 'Commerciale' },
  { value: 'operator', label: 'Operatore' },
  { value: 'client', label: 'Cliente (Readonly)' },
];

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  sales_manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  sales_rep: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  operator: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  client: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

function UserAvatar({ name, size = 'md' }) {
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  const sz = size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-9 h-9 text-xs';
  return (
    <div className={`${sz} rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0`}>
      <span className="font-bold text-primary">{initials}</span>
    </div>
  );
}

const STATUS_STYLES = {
  active:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  suspended: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
};

const MODULES_INFO = [
  { key: 'pipeline',    label: 'Pipeline',          desc: 'Gestione opportunità e trattative commerciali',  icon: TrendingUp,   defaultOn: true  },
  { key: 'calendar',    label: 'Calendario',         desc: 'Calendario eventi, visite e appuntamenti',       icon: Calendar,     defaultOn: true  },
  { key: 'activities',  label: 'Attività',            desc: 'Task e to-do del team commerciale',              icon: CheckSquare,  defaultOn: true  },
  { key: 'orders',      label: 'Ordini & DDT',        desc: 'Ordini, documenti di trasporto e fatturazione',  icon: ShoppingCart, defaultOn: true  },
  { key: 'analytics',   label: 'Analisi',             desc: 'Analytics ordini e performance vendite',         icon: BarChart2,    defaultOn: true  },
  { key: 'reports',     label: 'Report',              desc: 'Generazione report PDF ed Excel',                icon: FileDown,     defaultOn: true  },
  { key: 'forecast',    label: 'Forecast & Budget',   desc: 'Budget commerciale per azienda e prodotto',      icon: Target,       defaultOn: false },
  { key: 'inventory',   label: 'Scorte',              desc: 'Materie prime, movimenti di magazzino e MRP',    icon: Warehouse,    defaultOn: false },
  { key: 'production',  label: 'Produzione',          desc: 'Distinta base, piani di produzione e MRP',       icon: Factory,      defaultOn: false },
];

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState({});
  const [showWizard, setShowWizard] = useState(false);

  // Dialogs
  const [editUserDialog, setEditUserDialog] = useState(null);
  // Admin password reset dialog: { id, name, email, newPassword, saving, done, result? }
  const [resetPwdDialog, setResetPwdDialog] = useState(null);

  // Password fields
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [slugCopied, setSlugCopied] = useState(false);

  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdError, setPwdError] = useState('');

  const [tenantForm, setTenantForm] = useState({
    name: '', logo_url: '',
    settings: {
      ragione_sociale: '', codice_fiscale: '', address: '', vat_number: '',
      phone: '', email: '', pec: '', website: ''
    }
  });
  const [editForm, setEditForm] = useState({ name: '', role: 'sales_rep' });

  const fetchData = async () => {
    try {
      // Fetch calls independently so a 404 on one doesn't blank the others
      const [tenantRes, usersRes, modulesRes] = await Promise.allSettled([
        axios.get(`${API}/admin/tenant`),
        axios.get(`${API}/users`),
        axios.get(`${API}/admin/my-modules`),
      ]);
      if (tenantRes.status === 'fulfilled') {
        setTenant(tenantRes.value.data);
        const s = tenantRes.value.data.settings || {};
        setTenantForm({
          name: tenantRes.value.data.name || '',
          logo_url: tenantRes.value.data.logo_url || '',
          settings: {
            ragione_sociale: s.ragione_sociale || '',
            codice_fiscale: s.codice_fiscale || '',
            address: s.address || '',
            vat_number: s.vat_number || '',
            phone: s.phone || '',
            email: s.email || '',
            pec: s.pec || '',
            website: s.website || '',
          },
        });
      }
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data);
      if (modulesRes.status === 'fulfilled') setModules(modulesRes.value.data || {});
    } catch {
      toast.error('Errore caricamento impostazioni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const saveTenant = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tenant`, tenantForm);
      toast.success('Impostazioni salvate');
      fetchData();
    } catch { toast.error('Errore'); } finally { setSaving(false); }
  };

  const saveEditUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/users/${editUserDialog.id}`, editForm);
      toast.success('Utente aggiornato');
      setEditUserDialog(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdError('');
    if (pwdForm.new_password !== pwdForm.confirm) { setPwdError('Le password non coincidono'); return; }
    if (pwdForm.new_password.length < 6) { setPwdError('Minimo 6 caratteri'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success('Password aggiornata');
      setPwdForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) { setPwdError(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const copySlug = async () => {
    await navigator.clipboard.writeText(tenant?.slug || '');
    setSlugCopied(true);
    setTimeout(() => setSlugCopied(false), 2000);
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await axios.patch(`${API}/users/${userId}/status`, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Utente riattivato' : 'Utente sospeso');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Eliminare l'utente "${name}"?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Utente eliminato');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const openEdit = (u) => {
    setEditForm({ name: u.name, role: u.role });
    setEditUserDialog(u);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Impostazioni</h1>
        <p className="text-muted-foreground text-sm mt-1">Configura il tuo spazio di lavoro</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="mb-6">
          <TabsTrigger value="users" data-testid="settings-users-tab">Utenti</TabsTrigger>
          <TabsTrigger value="tenant" data-testid="settings-tenant-tab">Azienda</TabsTrigger>
          <TabsTrigger value="modules" data-testid="settings-modules-tab">
            <LayoutGrid size={13} className="mr-1.5" />Moduli
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-security-tab">Sicurezza</TabsTrigger>
        </TabsList>

        {/* ── USERS TAB ── */}
        <TabsContent value="users">
          <div className="space-y-4">

            {/* Invite CTA */}
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail size={14} className="text-primary flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Invita un nuovo utente</p>
                      <p className="text-xs text-muted-foreground">Genera un link di registrazione sicuro con ruolo preassegnato</p>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => navigate('/invitations')} data-testid="go-to-invitations-btn">
                    <UserPlus size={13} className="mr-1.5" />Gestisci Inviti
                    <ExternalLink size={11} className="ml-1.5 opacity-60" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Users list */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Team ({users.length})
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-0 pb-2">
                <div className="divide-y divide-border">
                  {users.map(u => {
                    const status = u.status || (u.is_active ? 'active' : 'suspended');
                    return (
                      <div key={u.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                        <UserAvatar name={u.name} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium">{u.name}</p>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ROLE_COLORS[u.role] || ROLE_COLORS.client}`}>
                              {ROLES.find(r => r.value === u.role)?.label || u.role}
                            </span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[status] || STATUS_STYLES.active}`}>
                              {status === 'active' ? 'Attivo' : 'Sospeso'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                          {u.last_login && <p className="text-[11px] text-muted-foreground/70">Ultimo accesso: {new Date(u.last_login).toLocaleDateString('it-IT')}</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Modifica">
                            <Edit2 size={13} />
                          </Button>
                          {u.id !== user?.id && (
                            <>
                              <Button variant="ghost" size="icon"
                                className="h-7 w-7 text-blue-600 hover:text-blue-700"
                                onClick={() => setResetPwdDialog({ id: u.id, name: u.name, email: u.email, newPassword: '', saving: false, done: false })}
                                data-testid={`reset-pwd-${u.id}`}
                                title="Reset password">
                                <KeyRound size={13} />
                              </Button>
                              <Button variant="ghost" size="icon"
                                className={`h-7 w-7 ${status === 'active' ? 'text-amber-500 hover:text-amber-600' : 'text-green-500 hover:text-green-600'}`}
                                onClick={() => toggleUserStatus(u.id, status)}
                                data-testid={`toggle-status-${u.id}`}
                                title={status === 'active' ? 'Sospendi' : 'Riattiva'}>
                                {status === 'active' ? <UserMinus size={13} /> : <UserCheck size={13} />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive/80"
                                onClick={() => deleteUser(u.id, u.name)} data-testid={`delete-user-${u.id}`}>
                                <Trash2 size={13} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {users.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">Nessun utente</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── TENANT TAB ── */}
        <TabsContent value="tenant">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
                  <SettingsIcon size={16} />Workspace
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={saveTenant} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Nome Workspace</Label>
                      <Input value={tenantForm.name}
                        onChange={e => setTenantForm(f => ({ ...f, name: e.target.value }))}
                        data-testid="tenant-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>URL Logo</Label>
                      <Input value={tenantForm.logo_url}
                        onChange={e => setTenantForm(f => ({ ...f, logo_url: e.target.value }))}
                        placeholder="https://..." />
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-sm font-medium">Piano</p>
                      <Badge variant="outline" className="mt-1 capitalize">{tenant?.plan || 'free'}</Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Slug</p>
                      <p className="text-sm text-muted-foreground font-mono mt-1">{tenant?.slug}</p>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FileText size={14} className="text-primary" />
                      <p className="text-sm font-semibold">Dati Azienda per DDT / Fatture</p>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">Questi dati appaiono nel footer DDT e nei documenti ufficiali.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1.5 md:col-span-2">
                        <Label className="text-xs">Ragione Sociale</Label>
                        <Input value={tenantForm.settings.ragione_sociale}
                          onChange={e => setTenantForm(f => ({ ...f, settings: { ...f.settings, ragione_sociale: e.target.value } }))}
                          placeholder="es. Azienda Agricola Rossi S.r.l." data-testid="tenant-ragione-sociale-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Codice Fiscale</Label>
                        <Input value={tenantForm.settings.codice_fiscale}
                          onChange={e => setTenantForm(f => ({ ...f, settings: { ...f.settings, codice_fiscale: e.target.value } }))}
                          data-testid="tenant-cf-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">P.IVA</Label>
                        <Input value={tenantForm.settings.vat_number}
                          onChange={e => setTenantForm(f => ({ ...f, settings: { ...f.settings, vat_number: e.target.value } }))}
                          data-testid="tenant-vat-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Telefono</Label>
                        <Input value={tenantForm.settings.phone}
                          onChange={e => setTenantForm(f => ({ ...f, settings: { ...f.settings, phone: e.target.value } }))} />
                      </div>
                      <div className="space-y-1.5 md:col-span-2">
                        <Label className="text-xs">Indirizzo completo</Label>
                        <Input value={tenantForm.settings.address}
                          onChange={e => setTenantForm(f => ({ ...f, settings: { ...f.settings, address: e.target.value } }))}
                          data-testid="tenant-address-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Email</Label>
                        <Input type="email" value={tenantForm.settings.email}
                          onChange={e => setTenantForm(f => ({ ...f, settings: { ...f.settings, email: e.target.value } }))}
                          data-testid="tenant-email-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">PEC</Label>
                        <Input value={tenantForm.settings.pec}
                          onChange={e => setTenantForm(f => ({ ...f, settings: { ...f.settings, pec: e.target.value } }))}
                          data-testid="tenant-pec-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Sito Web</Label>
                        <Input value={tenantForm.settings.website}
                          onChange={e => setTenantForm(f => ({ ...f, settings: { ...f.settings, website: e.target.value } }))} />
                      </div>
                    </div>
                    {(tenantForm.name || tenantForm.settings.vat_number) && (
                      <div className="mt-3 p-3 bg-muted/40 rounded-lg border border-dashed border-border">
                        <p className="text-xs text-muted-foreground mb-1">Anteprima footer DDT:</p>
                        <p className="text-xs font-mono text-muted-foreground">
                          {[
                            tenantForm.settings.ragione_sociale || tenantForm.name,
                            tenantForm.settings.address,
                            [
                              tenantForm.settings.codice_fiscale ? `C.F.: ${tenantForm.settings.codice_fiscale}` : '',
                              tenantForm.settings.vat_number ? `P.IVA: ${tenantForm.settings.vat_number}` : '',
                            ].filter(Boolean).join(' — '),
                            tenantForm.settings.email ? `mail: ${tenantForm.settings.email}` : '',
                            tenantForm.settings.pec ? `Pec: ${tenantForm.settings.pec}` : '',
                          ].filter(Boolean).join(' — ')}
                        </p>
                      </div>
                    )}
                  </div>

                  <Button type="submit" disabled={saving} data-testid="save-tenant-btn">
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Salva Impostazioni
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── MODULES TAB ── */}
        <TabsContent value="modules">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
                      <LayoutGrid size={18} />Moduli Attivi
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Panoramica dei moduli abilitati per la tua azienda. Per modificarli, contatta il Super Admin.
                    </p>
                  </div>
                  {user?.role === 'admin' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowWizard(true)}
                      className="gap-2 flex-shrink-0"
                      data-testid="reopen-wizard-btn"
                    >
                      <Sparkles size={14} />
                      Wizard configurazione
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {MODULES_INFO.map(m => {
                    const on = modules[m.key] !== undefined ? modules[m.key] === true : m.defaultOn;
                    const Icon = m.icon;
                    return (
                      <div key={m.key}
                        className={`flex items-start gap-3 p-3.5 rounded-lg border transition-all
                          ${on
                            ? 'bg-background border-border'
                            : 'bg-muted/20 border-dashed border-muted-foreground/20 opacity-60'
                          }`}
                        data-testid={`module-status-${m.key}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                          ${on ? 'bg-primary/10' : 'bg-muted/50'}`}>
                          <Icon size={16} className={on ? 'text-primary' : 'text-muted-foreground/40'} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold leading-tight">{m.label}</p>
                            {on
                              ? <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                              : <XCircle size={14} className="text-muted-foreground/40 flex-shrink-0" />
                            }
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{m.desc}</p>
                          <span className={`inline-block text-[10px] font-medium mt-1.5 px-1.5 py-0.5 rounded-full
                            ${on
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-muted text-muted-foreground'
                            }`}>
                            {on ? 'Attivo' : 'Non attivo'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
                  <Shield size={11} />
                  I moduli vengono gestiti dal Super Amministratore. Contattaci per abilitare nuove funzionalità.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── SECURITY TAB ── */}
        <TabsContent value="security">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
                  <KeyRound size={18} />Cambia Password
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={changePassword} className="space-y-4 max-w-sm">
                  <div className="space-y-1.5">
                    <Label>Password attuale *</Label>
                    <div className="relative">
                      <Input type={showCurrentPwd ? 'text' : 'password'} value={pwdForm.current_password}
                        onChange={e => setPwdForm(f => ({ ...f, current_password: e.target.value }))}
                        data-testid="current-pwd-input" required />
                      <button type="button" onClick={() => setShowCurrentPwd(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showCurrentPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Nuova password *</Label>
                    <div className="relative">
                      <Input type={showNewPwd ? 'text' : 'password'} value={pwdForm.new_password}
                        onChange={e => setPwdForm(f => ({ ...f, new_password: e.target.value }))}
                        data-testid="new-pwd-input" required />
                      <button type="button" onClick={() => setShowNewPwd(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showNewPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">Minimo 6 caratteri</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Conferma nuova password *</Label>
                    <Input type="password" value={pwdForm.confirm}
                      onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))}
                      data-testid="confirm-pwd-input" required />
                  </div>
                  {pwdError && <Alert variant="destructive"><AlertDescription>{pwdError}</AlertDescription></Alert>}
                  <Button type="submit" disabled={saving} data-testid="change-pwd-btn">
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Aggiorna password
                  </Button>
                </form>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
                  <Shield size={18} />Sicurezza Avanzata
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium">Autenticazione a 2 fattori (2FA)</p>
                    <p className="text-xs text-muted-foreground">Aumenta la sicurezza con TOTP</p>
                  </div>
                  <Badge variant="outline" className="text-xs">Prossimamente</Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium">Log attività</p>
                    <p className="text-xs text-muted-foreground">Traccia le operazioni sensibili</p>
                  </div>
                  <Badge variant="outline" className="text-xs">Prossimamente</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── EDIT USER DIALOG ── */}
      <Dialog open={!!editUserDialog} onOpenChange={() => setEditUserDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Modifica Utente</DialogTitle>
          </DialogHeader>
          {editUserDialog && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setSaving(true);
              try {
                await axios.put(`${API}/users/${editUserDialog.id}`, editForm);
                toast.success('Utente aggiornato');
                setEditUserDialog(null);
                fetchData();
              } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
            }} className="space-y-4 mt-2">
              <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg">
                <UserAvatar name={editUserDialog.name} />
                <div>
                  <p className="text-sm font-medium">{editUserDialog.name}</p>
                  <p className="text-xs text-muted-foreground">{editUserDialog.email}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Nome *</Label>
                <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required data-testid="edit-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Ruolo</Label>
                <Select value={editForm.role} onValueChange={v => setEditForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setEditUserDialog(null)}>Annulla</Button>
                <Button type="submit" className="flex-1" disabled={saving} data-testid="save-edit-user-btn">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva modifiche
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Reset Password Dialog */}
      <Dialog open={!!resetPwdDialog} onOpenChange={() => setResetPwdDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              <KeyRound size={18} className="inline mr-2" />
              Reset Password Utente
            </DialogTitle>
          </DialogHeader>
          {resetPwdDialog && !resetPwdDialog.done && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!resetPwdDialog.newPassword || resetPwdDialog.newPassword.length < 8) {
                  toast.error('La password deve avere almeno 8 caratteri');
                  return;
                }
                setResetPwdDialog(d => ({ ...d, saving: true }));
                try {
                  const r = await axios.post(`${API}/users/${resetPwdDialog.id}/reset-password`, {
                    new_password: resetPwdDialog.newPassword,
                  });
                  setResetPwdDialog(d => ({ ...d, saving: false, done: true, result: r.data }));
                  toast.success('Password aggiornata');
                } catch (e2) {
                  toast.error(e2.response?.data?.detail || 'Errore reset password');
                  setResetPwdDialog(d => ({ ...d, saving: false }));
                }
              }}
              className="space-y-4"
            >
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm">
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  Stai resettando la password di <span className="font-bold">{resetPwdDialog.name}</span>
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-0.5">{resetPwdDialog.email}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Nuova Password (min 8 caratteri) *</Label>
                <Input
                  type="text"
                  value={resetPwdDialog.newPassword}
                  onChange={e => setResetPwdDialog(d => ({ ...d, newPassword: e.target.value }))}
                  placeholder="Es. Cambio2025!"
                  autoFocus
                  minLength={8}
                  data-testid="admin-reset-password-input"
                />
                <div className="flex gap-2">
                  <Button
                    type="button" variant="outline" size="sm" className="text-xs h-7"
                    onClick={() => {
                      // Generate a strong random password
                      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
                      const pwd = Array.from({length: 12}, () => chars[Math.floor(Math.random() * chars.length)]).join('') + '!';
                      setResetPwdDialog(d => ({ ...d, newPassword: pwd }));
                    }}
                  >Genera password sicura</Button>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setResetPwdDialog(null)}>Annulla</Button>
                <Button type="submit" className="flex-1" disabled={resetPwdDialog.saving} data-testid="confirm-reset-pwd-btn">
                  {resetPwdDialog.saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Conferma Reset
                </Button>
              </div>
            </form>
          )}
          {resetPwdDialog?.done && (
            <div className="space-y-4">
              <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3">
                <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                  ✓ Password aggiornata con successo
                </p>
                <p className="text-xs text-emerald-700 dark:text-emerald-300/80 mt-1">
                  Comunica la nuova password a <b>{resetPwdDialog.name}</b> tramite un canale sicuro (WhatsApp, telefono).
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Nuova Password (copia e comunica)</Label>
                <div className="flex gap-2">
                  <Input readOnly value={resetPwdDialog.result?.new_password || ''} className="font-mono" data-testid="new-password-display" />
                  <Button
                    type="button" variant="outline" size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(resetPwdDialog.result?.new_password || '');
                      toast.success('Password copiata negli appunti');
                    }}
                    title="Copia negli appunti"
                  >
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <Button className="w-full" onClick={() => setResetPwdDialog(null)}>Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Setup Wizard (re-trigger) */}
      {showWizard && (
        <SetupWizard
          tenantName={tenant?.name || 'la tua azienda'}
          onClose={() => setShowWizard(false)}
        />
      )}
    </div>
  );
}
